/**
 * Tâche 1343 (Eric 2026-06-30) — Panneau des raccourcis clavier.
 *
 * Les raccourcis de la tâche 1338 (Alt + flèches, Alt + Entrée) ne servent à
 * rien si personne ne sait qu'ils existent : une infobulle ne se lit qu'au
 * survol du bouton qu'on cherchait justement à éviter. Le panneau s'ouvre
 * donc tout seul la PREMIÈRE fois qu'on arrive sur la transcription
 * fusionnée, et reste accessible par le bouton d'aide de la barre.
 */
import React from "react";
import { X, Keyboard } from "@phosphor-icons/react";

export const CLE_RACCOURCIS_VUS = "knoria_raccourcis_fusion_vus";

const RACCOURCIS = [
  {
    touches: ["Alt", "→"],
    fr: "Aller à la correction non traitée suivante",
    en: "Go to the next unhandled correction",
  },
  {
    touches: ["Alt", "←"],
    fr: "Revenir à la correction non traitée précédente",
    en: "Go back to the previous unhandled correction",
  },
  {
    touches: ["Alt", "Entrée"],
    fr: "Accepter la correction de la phrase en surbrillance",
    en: "Accept the correction on the highlighted sentence",
  },
  {
    // Tâche 1350 (Eric 2026-06-30) — Sans le refus au clavier, une relecture
    // s'arrêtait net dès la première correction à rejeter.
    touches: ["Alt", "Retour arrière"],
    fr: "Refuser la correction de la phrase en surbrillance",
    en: "Reject the correction on the highlighted sentence",
  },
];

export default function RaccourcisClavierModal({ open, onClose, lang = "fr" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="raccourcis-clavier-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
          <div>
            <h3 className="font-bold text-base text-zinc-900 flex items-center gap-2">
              <Keyboard size={18} weight="bold" className="text-[#002FA7]" />
              {lang === "fr" ? "Raccourcis clavier" : "Keyboard shortcuts"}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Pour relire une longue transcription sans quitter le clavier."
                : "To review a long transcript without leaving the keyboard."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-200 rounded"
            data-testid="close-raccourcis-btn"
          >
            <X size={18} />
          </button>
        </div>
        <ul className="divide-y divide-zinc-100" data-testid="raccourcis-liste">
          {RACCOURCIS.map((r) => (
            <li key={r.touches.join("+")} className="px-5 py-3 flex items-center justify-between gap-4">
              <span className="text-[12.5px] text-zinc-700">{lang === "fr" ? r.fr : r.en}</span>
              <span className="flex items-center gap-1 shrink-0">
                {r.touches.map((k) => (
                  <kbd
                    key={k}
                    className="px-2 py-1 rounded border border-zinc-300 bg-zinc-50 text-[11px] font-semibold text-zinc-700 shadow-[0_1px_0_#d4d4d8]"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3">
          <p className="text-[11px] text-zinc-500">
            {lang === "fr"
              ? "Les raccourcis sont ignorés pendant que vous tapez dans un champ de texte."
              : "Shortcuts are ignored while you type in a text field."}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001F7A] shrink-0"
            data-testid="raccourcis-compris-btn"
          >
            {lang === "fr" ? "C'est compris" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
